import React, { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { ContentStateProperty, InputType } from '../../../../@types/types'
import { cutToChosenDigitLength } from '../../../../services/conversion/conversionServices'
import { InputProps } from '../../../../@types/props-types'
import { useIsRequiredProperty } from '../../../../services/validation/requiredFieldsUtils/useIsRequiredProperty'
import { useFormState } from '../../../pages/insertionPage/wizard/useFormState'
import { TextInput as CosmaTextInput } from 'is24-corecss'
import { mapUnitTypeToUnitSymbol } from '../unitSymbols/unitSymbols'
import styles from './TextInput.module.css'

export interface TextInputProps extends InputProps {
  type?: InputType
  className?: string
  style?: React.CSSProperties
  placeholder?: string
  label?: string | React.ReactNode
  ariaLabel?: string
  value?: string | number
  maxLength?: number
  showMaxLengthLabel?: boolean
  errors: string[]
  disabled?: boolean
  affixRight?: 'none' | 'unit' | 'icon'
  unitRight?: UnitRight
  iconRight?: string | React.ReactNode
  hideMandatorySign?: boolean
  hideErrorMessageWithValidation?: boolean
  showRedBoarder?: boolean
  doOnFieldChange: (field: ContentStateProperty, value: string | undefined) => void
  onBlur?: () => void
  onBlurTracking?: (field: ContentStateProperty, value: string, isUnchanged: boolean) => void
  onFocus?: () => void
  dataHeyimmoContext?: string
}

export type UnitRight =
  | 'none'
  | 'percent'
  | 'sqm'
  | 'eur'
  | 'eurPerSqm'
  | 'years'
  | 'months'
  | 'minutes'
  | 'kilowattHour'
  | 'kgPerSqm'
  | 'kiloVoltAmpere'
  | 'kg'
  | 't'
  | 'meters'

export const TextInput: React.FunctionComponent<TextInputProps> = (props: TextInputProps) => {
  const { t } = useTranslation()
  const { setTouched, formState } = useFormState()
  const [state, setState] = useState<{
    isUnchanged: boolean
    lastValue: string | number | undefined
    currentValue: string | number | undefined
  }>({
    isUnchanged: true,
    lastValue: props.value,
    currentValue: props.value,
  })

  React.useEffect(() => setState((prev) => ({ ...prev, currentValue: props.value })), [props.value])

  const isFieldMandatory = useIsRequiredProperty(props.propertyName)

  const onLostFocus = ({ relatedTarget, currentTarget }: React.FocusEvent<HTMLInputElement>) => {
    const focusChangedToOtherThanFieldContainer = relatedTarget?.className !== currentTarget?.parentElement?.className

    if (focusChangedToOtherThanFieldContainer) {
      props.onBlur && props.onBlur()
      setTouched(props.propertyName)
    }

    const newValue = currentTarget.value
    const valueChanged = state.lastValue !== newValue
    const meaningfulValueSet = !(state.lastValue === undefined && newValue === '')

    if (valueChanged && meaningfulValueSet && focusChangedToOtherThanFieldContainer) {
      props.onBlurTracking && props.onBlurTracking(props.propertyName, newValue, state.isUnchanged)
      setState((prev) => ({ ...prev, lastValue: newValue, isUnchanged: false }))
    }
  }

  const doChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    let newValue: string = value
    if (props.maxLength !== undefined) {
      newValue = cutToChosenDigitLength(value, props.maxLength) || ''
    }
    props.doOnFieldChange(props.propertyName, newValue)
    setState((prev) => ({ ...prev, currentValue: newValue }))
  }

  const showErrors = () =>
    props.errors.length > 0 && (formState.submitCount > 0 || formState.touched[props.propertyName])
  const style = props.style ? props.style : ({} as React.CSSProperties)

  const isSuccess = props.value !== undefined && props.value !== '' && !showErrors() && !props.showRedBoarder

  const valueLength = props.value ? String(props.value).length : undefined
  const remainingCharacters = props.maxLength && valueLength ? props.maxLength - valueLength : String(props.maxLength)
  const renderRemainingCharacters = props.showMaxLengthLabel ? (
    <span className={`float-right font-body-small-regular font-lightgray ${styles.helperTextStyle}`}>
      {t('message.maxLengthLabel', `${remainingCharacters} characters remaining`, {
        value: remainingCharacters,
      })}
    </span>
  ) : undefined

  const invalid = (showErrors() && props.hideErrorMessageWithValidation !== true) || props.showRedBoarder
  const showMandatorySign = !props.hideMandatorySign && isFieldMandatory

  const textInput = (
    <CosmaTextInput.Root
      id={props.id}
      className={props.className}
      style={showErrors() || props.showRedBoarder ? {} : style}
      {...(props.disabled && { disabled: true })}
      required={showMandatorySign}
      invalid={invalid}
      success={isSuccess}
    >
      {props.label && <CosmaTextInput.Label>{props.label}</CosmaTextInput.Label>}
      <CosmaTextInput.Input
        tabIndex={0}
        type={props.type}
        placeholder={props.placeholder ?? ''}
        endElement={props.iconRight || mapUnitTypeToUnitSymbol(props.unitRight)}
        value={state.currentValue ?? ''}
        autoComplete='nope'
        onChange={doChange}
        onBlur={onLostFocus}
        {...(props.onFocus && { onFocus: props.onFocus })}
        {...(props.maxLength && { maxLength: props.maxLength })}
        {...(props.ariaLabel && { 'aria-label': props.ariaLabel })}
      />
      <CosmaTextInput.HelperText>{renderRemainingCharacters}</CosmaTextInput.HelperText>
      <CosmaTextInput.ErrorText>{t(`validation.${props.errors[0]}`)}</CosmaTextInput.ErrorText>
    </CosmaTextInput.Root>
  )

  return (
    <>
      {textInput}
      {props.dataHeyimmoContext && (
        <span data-heyimmo-context={props.dataHeyimmoContext} style={{ display: 'none' }} aria-hidden='true'>
          {state.currentValue ?? ''}
        </span>
      )}
    </>
  )
}
